import React, { useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { Field, ErrorMessage, useFormikContext } from 'formik';
import clsx from 'clsx';

import { ContactFormData } from '../../FormData/config';
import type { FormFields } from '../ContactFormJSON.types';
import ErrorIcon from '../ErrorIcon';
import styles from './InputFields.module.scss';

interface InputFieldsProps {
  data: FormFields;
  formErrors: string | undefined;
  formTouched: boolean | undefined;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

export default function InputFields(props: InputFieldsProps) {
  const { formErrors, formTouched, setFieldTouched, setFieldValue } = props;
  const { values } = useFormikContext<ContactFormData>();
  const {
    type,
    name,
    required,
    label,
    maxLength,
    validationMessage,
    placeholder,
  } = props.data;
  const [characterCount, setCharacterCount] = useState(0);

  const sharedProps = {
    id: name,
    name: name,
    required: required,
    'aria-required': required,
    'aria-invalid': formErrors && formTouched ? 'true' : 'false',
    'data-touched': formTouched ? 'true' : 'false',
    placeholder: placeholder,
  };

  const renderErrorMessage = (props: { name: keyof ContactFormData }) => (
    <ErrorMessage name={props.name}>
      {() => (
        <div
          className={clsx(styles.errorMessage, 'input-field__error-message')}
          aria-live="polite"
        >
          <ErrorIcon />
          <small id={`error__${props.name}`}>
            {formErrors || validationMessage}
          </small>
        </div>
      )}
    </ErrorMessage>
  );

  // Pass props.data.type instead of type to resolve typescript warnings
  switch (props.data.type) {
    case 'checkbox':
      return (
        <>
          <div
            data-testid={`form__field--${name}`}
            className={'input-field__checkbox-container'}
          >
            <Field
              {...sharedProps}
              type={type}
              className={'input-field__checkbox-element'}
              onChange={() => {
                setFieldTouched(name, true);
                setFieldValue(name, !values[name]);
              }}
            />
            <div className={'input-field__checkbox-square'}></div>
            <label
              htmlFor={`${String(name)}`}
              className={'input-field__label-element'}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label) }}
            />
          </div>
          {renderErrorMessage({ name })}
        </>
      );
    case 'textarea':
      return (
        <>
          <div className={'input-field__text-area-container'}>
            <label htmlFor={name} className={'input-field__label-element'}>
              {label}
            </label>
            <Field
              {...sharedProps}
              component={type}
              className={'input-field__text-area-element'}
              maxLength={maxLength}
              onKeyUp={(e: { target: { value: string } }) =>
                setCharacterCount(e.target.value.length)
              }
              data-testid={`form__field--${name}`}
            />
            <span className={'input-field__character-count'}>
              {`${characterCount} / ${maxLength} Char`}
            </span>
          </div>
          {renderErrorMessage({ name })}
        </>
      );
    case 'select':
      return (
        <>
          <label htmlFor={name} className={'input-field__label-element'}>
            {label}
          </label>
          <Field
            {...sharedProps}
            component={type}
            className={'input-field__select-element'}
            autoComplete={props.data.autocomplete}
            data-testid={`form__field--${name}`}
          >
            {props.data.optgroups?.map(function (optgroup, index) {
              {
                const options = optgroup.options?.map(function (option, index) {
                  return (
                    <option
                      value={option.value}
                      disabled={!option.value || option.value == 'default'}
                      key={`${option.label}--${index}`}
                    >
                      {option.label}
                    </option>
                  );
                });

                return optgroup.label ? (
                  <optgroup
                    label={optgroup.label}
                    key={`${optgroup.label}--${index}`}
                  >
                    {options}
                  </optgroup>
                ) : (
                  options
                );
              }
            })}
          </Field>
          {renderErrorMessage({ name })}
        </>
      );
    case 'email':
      return (
        <>
          <label htmlFor={name} className={'input-field__label-element'}>
            {label}
          </label>
          <Field
            {...sharedProps}
            type={type}
            className={'input-field__input-element'}
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
            autoComplete={props.data.autocomplete}
            data-testid={`form__field--${name}`}
          />
          {renderErrorMessage({ name })}
        </>
      );
    case 'hidden':
      return (
        <>
          <label htmlFor={name} className={styles.visuallyHidden}>
            {label}
          </label>
          <Field
            {...sharedProps}
            type={type}
            data-testid={`form__field__hidden--${name}`}
          />
          {renderErrorMessage({ name })}
        </>
      );
    default:
      return (
        <>
          <label htmlFor={name} className={'input-field__label-element'}>
            {label}
          </label>
          <Field
            {...sharedProps}
            type={type}
            className={'input-field__input-element'}
            maxLength={maxLength ? maxLength : ''}
            autoComplete={props.data.autocomplete}
            data-testid={`form__field--${name}`}
          />
          {renderErrorMessage({ name })}
        </>
      );
  }
}
